import { formatJobNumber, visitLockReason, visitLocked, visitUnlockable } from '@fieldops/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Component, useMemo, useState, type ReactNode } from 'react'
import { cn } from '../../lib/cn.js'
import { formatTime, initials } from '../../lib/format.js'
import { useJobOverlay } from '../../lib/job-overlay.js'
import { useIsTablet } from '../../lib/media.js'
import { useSession } from '../../lib/session.js'
import { errorMessage, useTRPC, type RouterOutputs } from '../../lib/trpc.js'
import { Icon } from '../Icon.js'
import { Modal } from '../Modal.js'
import { Sheet } from '../Sheet.js'
import { MapCanvas } from '../map/MapCanvas.js'
import type { MapPin } from '../map/surface.js'
import { Avatar, EmptyState, ErrorState, SkeletonRows, Spinner } from '../ui.js'
import {
  FieldScreen,
  Group,
  GroupLabel,
  ScreenHeader,
  VisitBadge,
  cardClass,
  lengthOf,
  rowClass,
  timeRange,
  todayWindow,
  workAndSuburb,
} from './kit.js'

type Visit = RouterOutputs['schedule']['board'][number]
type Person = RouterOutputs['users']['schedulable'][number]

function isLive(visit: Visit): boolean {
  return visit.status !== 'CANCELLED' && visit.status !== 'NO_SHOW'
}

function assignedTo(visit: Visit, userId: string): boolean {
  return visit.assignments.some((assignment) => assignment.userId === userId)
}

/** Today's board and the people on it, shared by the list and the map. */
function useTeamDay() {
  const trpc = useTRPC()
  const range = useMemo(todayWindow, [])
  const board = useQuery(trpc.schedule.board.queryOptions(range))
  const people = useQuery(trpc.users.schedulable.queryOptions())
  const live = (board.data ?? []).filter(isLive)
  return { board, people, live }
}

/**
 * Where somebody is, as far as the schedule knows: the job they are at or
 * driving to, else the next one, else the last one they finished.
 */
function whereabouts(visits: Visit[]): { visit: Visit; state: string } | null {
  const active = visits.find((visit) => visit.status === 'ON_SITE')
  if (active) return { visit: active, state: 'On site' }
  const driving = visits.find((visit) => visit.status === 'EN_ROUTE')
  if (driving) return { visit: driving, state: 'En route to' }
  const next = visits.find((visit) => visit.status !== 'COMPLETED')
  if (next) return { visit: next, state: `Next · ${formatTime(next.startAt)}` }
  const last = visits.at(-1)
  return last ? { visit: last, state: 'Day done' } : null
}

/** The board's colours (`bookingLook`): Eco Green while somebody is there, Charcoal on the way, Green Deep once the day is done. */
const STATE_DOT: Record<string, string> = {
  'On site': 'bg-brand-500',
  'En route to': 'bg-ink-900',
  'Day done': 'bg-brand-700',
}

/**
 * The office's view of the day, on a phone or an iPad.
 *
 * Unassigned work first, in red, because it is the only thing on the screen
 * somebody is expected to fix. Then each person's day as one card. A row opens
 * the assign sheet — time, crew and confirmation — which writes through the
 * board's own `moveVisit` and `updateVisit`, so a confirmed booking is as
 * locked here as it is on the board.
 */
export function FieldTeam() {
  const tablet = useIsTablet()
  const { board, people, live } = useTeamDay()
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const unassigned = live.filter((visit) => visit.assignments.length === 0)
  const byTech = (people.data ?? [])
    .map((person) => ({
      person,
      visits: live.filter((visit) => assignedTo(visit, person.id)),
    }))
    .filter((entry) => entry.visits.length > 0)
  const selected = live.find((visit) => visit.id === selectedId) ?? null

  return (
    <FieldScreen>
      <ScreenHeader
        eyebrow={new Date().toLocaleDateString('en-AU', {
          weekday: 'long',
          day: 'numeric',
          month: 'long',
        })}
        title="Team"
      />

      {tablet ? null : (
        <div className={cn(cardClass, 'h-[210px]')}>
          <TeamMap />
        </div>
      )}

      {board.error ? (
        <div className={cardClass}>
          <ErrorState message={errorMessage(board.error)} onRetry={() => void board.refetch()} />
        </div>
      ) : board.isPending || people.isPending ? (
        <div className={cardClass}>
          <SkeletonRows rows={4} />
        </div>
      ) : (
        <>
          {unassigned.length > 0 ? (
            <Group label="Needs a tech" tone="red" aside={String(unassigned.length)}>
              {unassigned.map((visit) => (
                <button
                  key={visit.id}
                  type="button"
                  onClick={() => setSelectedId(visit.id)}
                  className={rowClass}
                >
                  <VisitTime visit={visit} />
                  <VisitText visit={visit} />
                  <span className="shrink-0 rounded-md bg-brand-500 px-3 py-1.5 text-[13px] font-semibold text-ink-900">
                    Assign
                  </span>
                </button>
              ))}
            </Group>
          ) : null}

          <section className="flex flex-col gap-2.5">
            <GroupLabel>Today, by tech</GroupLabel>
            {byTech.length === 0 ? (
              <div className={cn(cardClass, 'py-4')}>
                <EmptyState title="Nobody is booked today" />
              </div>
            ) : (
              byTech.map(({ person, visits }) => {
                const where = whereabouts(visits)
                return (
                  <div key={person.id} className={cn(cardClass, 'divide-y divide-ink-200')}>
                    <div className="flex items-center gap-3 px-4 py-3">
                      <Avatar
                        name={person.name}
                        colour={person.colour}
                        className="size-10 text-[13px]"
                      />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-[16px] font-semibold text-ink-900">
                          {person.name}
                        </p>
                        {where ? (
                          <p className="flex items-center gap-1.5 truncate text-[13px] text-ink-500">
                            <span
                              className={cn(
                                'size-2 shrink-0 rounded-[2px]',
                                STATE_DOT[where.state] ?? 'bg-ink-400',
                              )}
                            />
                            <span className="truncate">
                              {where.state}
                              {where.state === 'Day done' ? '' : ` ${where.visit.job.client.name}`}
                            </span>
                          </p>
                        ) : null}
                      </div>
                      <span className="shrink-0 text-[13px] text-ink-500">
                        {visits.length} {visits.length === 1 ? 'job' : 'jobs'}
                      </span>
                    </div>
                    {visits.map((visit) => (
                      <button
                        key={visit.id}
                        type="button"
                        onClick={() => setSelectedId(visit.id)}
                        className={rowClass}
                      >
                        <VisitTime visit={visit} />
                        <VisitText visit={visit} />
                        <VisitBadge status={visit.status} />
                      </button>
                    ))}
                  </div>
                )
              })
            )}
          </section>
        </>
      )}

      {selected ? (
        <AssignSheet
          visit={selected}
          people={people.data ?? []}
          day={live}
          onClose={() => setSelectedId(null)}
        />
      ) : null}
    </FieldScreen>
  )
}

function VisitTime({ visit }: { visit: Visit }) {
  return (
    <div className="w-[70px] shrink-0 whitespace-nowrap">
      <p className="text-[15px] font-semibold tabular-nums text-ink-900">
        {formatTime(visit.startAt)}
      </p>
      <p className="text-xs tabular-nums text-ink-500">{lengthOf(visit.startAt, visit.endAt)}</p>
    </div>
  )
}

function VisitText({ visit }: { visit: Visit }) {
  return (
    <div className="min-w-0 flex-1">
      <p className="truncate text-[16px] font-semibold text-ink-900">{visit.job.client.name}</p>
      <p className="truncate text-[13px] text-ink-500">
        {workAndSuburb(visit.job, visit.job.site?.address) || formatJobNumber(visit.job)}
      </p>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Map                                                                        */
/* -------------------------------------------------------------------------- */

function pinElement(person: Person, label: string | null): HTMLElement {
  const element = document.createElement('div')
  element.className = 'flex items-center gap-1.5'
  const dot = document.createElement('span')
  dot.className =
    'grid size-8 place-items-center rounded-full text-[11px] font-bold text-white shadow-md ring-2 ring-white'
  dot.style.backgroundColor = person.colour ?? '#6d7570'
  dot.textContent = initials(person.name)
  element.append(dot)
  if (label) {
    const tag = document.createElement('span')
    tag.className =
      'rounded-md bg-white px-1.5 py-0.5 text-[12px] font-semibold whitespace-nowrap text-ink-900 shadow-md'
    tag.textContent = label
    element.append(tag)
  }
  element.title = person.name
  return element
}

/**
 * A pin per person at the job the schedule puts them at. There is no GPS in
 * FieldOps, so the map says so rather than implying a live position.
 */
export function TeamMap({ large = false }: { large?: boolean }) {
  const { live, people } = useTeamDay()

  const pins = useMemo<MapPin[]>(() => {
    const result: MapPin[] = []
    for (const person of people.data ?? []) {
      const where = whereabouts(live.filter((visit) => assignedTo(visit, person.id)))
      const address = where?.visit.job.site?.address
      if (!where || address?.latitude == null || address.longitude == null) continue
      result.push({
        id: person.id,
        lngLat: [address.longitude, address.latitude],
        element: pinElement(person, large ? person.name.split(/\s+/)[0]! : null),
      })
    }
    return result
  }, [live, people.data, large])

  return (
    <div className={cn('relative size-full', large && 'min-h-0 flex-1')}>
      <MapFailsQuietly>
        <MapCanvas pins={pins} className="absolute inset-0" />
      </MapFailsQuietly>
      <p className="pointer-events-none absolute bottom-2 left-2 rounded-md bg-white/90 px-2 py-1 text-[11px] font-medium text-ink-600 shadow-sm">
        Each pin is where the schedule has them, not a live location
      </p>
    </div>
  )
}

/**
 * A device that cannot draw the map (no WebGL) loses the map, not the screen.
 * The list of unassigned work under it is the part the office came for.
 */
class MapFailsQuietly extends Component<{ children: ReactNode }, { failed: boolean }> {
  override state = { failed: false }

  static getDerivedStateFromError() {
    return { failed: true }
  }

  override render() {
    if (!this.state.failed) return this.props.children
    return (
      <p className="absolute inset-0 grid place-items-center bg-ink-100 text-[13px] text-ink-500">
        The map could not be drawn on this device.
      </p>
    )
  }
}

/* -------------------------------------------------------------------------- */
/* Assign                                                                     */
/* -------------------------------------------------------------------------- */

function addMinutes(value: Date | string, minutes: number): Date {
  return new Date(new Date(value).getTime() + minutes * 60_000)
}

function AssignSheet({
  visit,
  people,
  day,
  onClose,
}: {
  visit: Visit
  people: Person[]
  day: Visit[]
  onClose: () => void
}) {
  const tablet = useIsTablet()
  const body = <AssignBody visit={visit} people={people} day={day} onClose={onClose} />

  if (tablet) {
    return (
      <Modal
        open
        onClose={onClose}
        labelledBy="assign-title"
        className="max-h-[min(760px,calc(100vh-2rem))] max-w-[520px] rounded-lg bg-ink-100"
      >
        {body}
      </Modal>
    )
  }

  return (
    <Sheet onClose={onClose} labelledBy="assign-title" className="bg-ink-100">
      {body}
    </Sheet>
  )
}

function AssignBody({
  visit,
  people,
  day,
  onClose,
}: {
  visit: Visit
  people: Person[]
  day: Visit[]
  onClose: () => void
}) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const { can } = useSession()
  const { openJob } = useJobOverlay()
  const tablet = useIsTablet()
  const canEdit = can('SCHEDULE_EDIT')
  const locked = visitLocked(visit.status)
  const lockReason = visitLockReason(visit.status)
  const assigned = visit.assignments.map((assignment) => assignment.userId)
  const crew = assigned.length > 1

  const settle = () => {
    void queryClient.invalidateQueries(trpc.schedule.pathFilter())
    void queryClient.invalidateQueries(trpc.jobs.pathFilter())
  }
  const move = useMutation(trpc.schedule.moveVisit.mutationOptions({ onSuccess: settle }))
  const update = useMutation(trpc.schedule.updateVisit.mutationOptions({ onSuccess: settle }))
  const failure = move.error ?? update.error

  const shift = (minutes: number) =>
    move.mutate({
      id: visit.id,
      startAt: addMinutes(visit.startAt, minutes),
      endAt: addMinutes(visit.endAt, minutes),
    })

  // A solo booking changes hands; a crew is edited a person at a time, because
  // replacing three people with one on a tap is not what anybody means.
  const pick = (userId: string | null) => {
    let userIds: string[]
    if (userId === null) userIds = []
    else if (crew) {
      userIds = assigned.includes(userId)
        ? assigned.filter((id) => id !== userId)
        : [...assigned, userId]
    } else userIds = assigned.includes(userId) ? assigned : [userId]
    move.mutate({ id: visit.id, startAt: visit.startAt, endAt: visit.endAt, userIds })
  }

  const mayMove = canEdit && !locked

  return (
    <div className="flex min-h-0 flex-col">
      <div className="flex flex-col gap-4 overflow-y-auto px-4 pt-3 pb-4">
        <div className="flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <h2 id="assign-title" className="text-[22px] leading-tight font-bold text-ink-900">
              {visit.job.client.name}
            </h2>
            <p className="mt-0.5 text-[14px] text-ink-500">
              {[workAndSuburb(visit.job, visit.job.site?.address), formatJobNumber(visit.job)]
                .filter(Boolean)
                .join(' · ')}
            </p>
          </div>
          <VisitBadge status={visit.status} />
        </div>

        <div className={cn(cardClass, 'flex items-center gap-2 p-2')}>
          <NudgeButton disabled={!mayMove || move.isPending} onClick={() => shift(-15)}>
            −15 min
          </NudgeButton>
          <p className="min-w-0 flex-1 text-center text-[15px] font-semibold tabular-nums text-ink-900">
            {timeRange(visit.startAt, visit.endAt)}
          </p>
          <NudgeButton disabled={!mayMove || move.isPending} onClick={() => shift(15)}>
            +15 min
          </NudgeButton>
        </div>

        {canEdit && lockReason ? (
          <div className="flex items-center gap-3 rounded-lg bg-white px-3.5 py-2.5 ring-1 ring-ink-300 ring-inset">
            <p className="min-w-0 flex-1 text-[13.5px] leading-snug text-ink-800">{lockReason}</p>
            {visitUnlockable(visit.status) ? (
              <button
                type="button"
                disabled={update.isPending}
                onClick={() => update.mutate({ id: visit.id, status: 'PROPOSED' })}
                className="shrink-0 rounded-md bg-ink-100 px-3 py-1.5 text-[13px] font-semibold text-ink-900 ring-1 ring-ink-200 ring-inset"
              >
                Unlock
              </button>
            ) : null}
          </div>
        ) : null}

        {failure ? (
          <p
            role="alert"
            className="rounded-lg bg-red-50 px-3.5 py-2.5 text-[13.5px] text-red-800 ring-1 ring-red-200 ring-inset"
          >
            {errorMessage(failure)}
          </p>
        ) : null}

        {canEdit ? (
          <section>
            <GroupLabel aside={crew ? `Crew of ${assigned.length}` : undefined}>
              Assign to
            </GroupLabel>
            <div className={cn(cardClass, 'divide-y divide-ink-200')}>
              {people.map((person) => {
                const load = day.filter((entry) => assignedTo(entry, person.id)).length
                const on = assigned.includes(person.id)
                return (
                  <button
                    key={person.id}
                    type="button"
                    disabled={!mayMove || move.isPending}
                    aria-pressed={on}
                    onClick={() => pick(person.id)}
                    className={cn(rowClass, 'disabled:opacity-60')}
                  >
                    <Avatar name={person.name} colour={person.colour} className="size-8 text-[11px]" />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[15px] font-medium text-ink-900">
                        {person.name}
                      </span>
                      <span className="block text-[12.5px] text-ink-500">
                        {load === 0 ? 'Free today' : `${load} ${load === 1 ? 'job' : 'jobs'} today`}
                      </span>
                    </span>
                    {on ? <Icon name="check" className="size-5 text-brand-600" /> : null}
                  </button>
                )
              })}
              <button
                type="button"
                disabled={!mayMove || move.isPending || assigned.length === 0}
                aria-pressed={assigned.length === 0}
                onClick={() => pick(null)}
                className={cn(rowClass, 'disabled:opacity-60')}
              >
                <span className="grid size-8 shrink-0 place-items-center rounded-md bg-red-50 text-red-700 ring-1 ring-red-200 ring-inset">
                  ?
                </span>
                <span className="min-w-0 flex-1 text-[15px] font-medium text-ink-900">
                  Needs a tech
                </span>
                {assigned.length === 0 ? (
                  <Icon name="check" className="size-5 text-brand-600" />
                ) : null}
              </button>
            </div>
          </section>
        ) : null}
      </div>

      <div className="flex shrink-0 gap-2 bg-white/92 px-4 pt-3 pb-[calc(var(--safe-bottom)+12px)] shadow-[inset_0_1px_0_var(--color-ink-200)]">
        <button
          type="button"
          onClick={() => {
            onClose()
            openJob(visit.job.id)
          }}
          className="h-12 flex-1 rounded-md bg-ink-100 text-[15px] font-semibold text-ink-900 ring-1 ring-ink-200 ring-inset"
        >
          Open job
        </button>
        {canEdit && visit.status === 'PROPOSED' ? (
          <button
            type="button"
            disabled={update.isPending}
            onClick={() => update.mutate({ id: visit.id, status: 'CONFIRMED' })}
            className="flex h-12 flex-1 items-center justify-center gap-2 rounded-md bg-brand-500 text-[15px] font-semibold text-ink-900 disabled:opacity-70"
          >
            {update.isPending ? <Spinner className="size-4" /> : null}
            Confirm booking
          </button>
        ) : null}
        {/* The phone's sheet has Done in its own header. */}
        {tablet ? (
          <button
            type="button"
            onClick={onClose}
            className="h-12 flex-1 rounded-md bg-ink-900 text-[15px] font-semibold text-white"
          >
            Done
          </button>
        ) : null}
      </div>
    </div>
  )
}

function NudgeButton({
  disabled,
  onClick,
  children,
}: {
  disabled: boolean
  onClick: () => void
  children: ReactNode
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className="h-10 shrink-0 rounded-md bg-ink-100 px-3 text-[14px] font-semibold text-ink-900 ring-1 ring-ink-200 ring-inset disabled:opacity-40"
    >
      {children}
    </button>
  )
}
